import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import { PWAFullGuide } from './PWAInstallGuide';
import { 
  BookOpen, Search, Video, FileText, Camera, Brain, 
  GraduationCap, Shield, BarChart3, AlertTriangle,
  CheckCircle, Users, Clock, Zap, HelpCircle,
  ChevronRight, ChevronDown, ExternalLink, PlayCircle, Download,
  Sparkles, TrendingUp, DollarSign, Languages, Activity,
  Loader2, ArrowRight, Image as ImageIcon, MessageSquare,
  Trophy, Plug, Radio, Target, Gauge, Eye, ClipboardCheck,
  Calendar, MapPin, Beaker, Bell, Mic, PieChart, Award, Lightbulb,
  Smartphone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedHelpCenter = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // AI-Powered Search Function
  const handleAISearch = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Enter a question",
        description: "Please type your question to get AI-powered help",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    setAiSearchResults(null);

    try {
      const response = await axios.post(`${API}/help/ai-search`, {
        query: searchQuery
      });

      setAiSearchResults(response.data);
      
      toast({
        title: "AI Assistant",
        description: "Found helpful information for your question"
      });
    } catch (error) {
      // Provide local search results as fallback
      const localResults = searchLocalContent(searchQuery);
      setAiSearchResults({
        answer: localResults.answer,
        related_topics: localResults.topics
      });
    } finally {
      setIsSearching(false);
    }
  };

  const searchLocalContent = (query) => {
    const q = query.toLowerCase();
    let answer = "Here's what I found related to your question:";
    let topics = [];

    if (q.includes('audit')) {
      answer = "Safety Audits allow you to conduct comprehensive OSHA inspections. Navigate to 'Audits' from the sidebar, click 'New Audit', select categories, and answer compliance questions.";
      topics = ['Creating Audits', 'Audit Categories', 'Compliance Scoring'];
    } else if (q.includes('training')) {
      answer = "The Training Center provides OSHA courses with interactive quizzes. Access it from the sidebar, browse modules, watch videos, and complete assessments to earn certificates.";
      topics = ['Training Modules', 'Quizzes', 'Certificates'];
    } else if (q.includes('gamification') || q.includes('badge') || q.includes('point')) {
      answer = "Gamification rewards safe behavior with points, badges, and leaderboards. Complete audits, training, and report observations to earn rewards and climb the rankings.";
      topics = ['Earning Points', 'Badge System', 'Leaderboards'];
    } else if (q.includes('integration') || q.includes('insurance') || q.includes('hr')) {
      answer = "The Integrations Hub connects your safety data with Insurance providers, HR systems (Workday, ADP), and Project Management tools (Jira, Asana). Configure API credentials in Settings.";
      topics = ['Insurance Integration', 'HR System Sync', 'Project Management'];
    } else if (q.includes('analytics') || q.includes('predictive') || q.includes('risk')) {
      answer = "Analytics & Intelligence provides predictive risk scoring, industry benchmarking, root cause analysis, and safety ROI tracking. Access real-time insights from the Analytics Hub.";
      topics = ['Risk Scoring', 'Benchmarking', 'ROI Dashboard'];
    } else if (q.includes('monitoring') || q.includes('sensor') || q.includes('iot')) {
      answer = "The Monitoring Hub tracks real-time data from IoT sensors, wearable devices, and geofenced zones. Configure alerts for environmental conditions and worker safety.";
      topics = ['IoT Sensors', 'Wearable Devices', 'Geofencing'];
    } else if (q.includes('voice') || q.includes('command')) {
      answer = "Voice Commands enable hands-free navigation. Click the microphone button (bottom-right), say commands like 'go to dashboard', 'report hazard', or 'start audit'.";
      topics = ['Voice Navigation', 'Supported Commands', 'Hands-free Mode'];
    }

    return { answer, topics };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAISearch();
    }
  };

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
    { id: 'mobile-app', name: 'Mobile App', icon: Smartphone, color: 'bg-indigo-100 text-indigo-700' },
    { id: 'core-features', name: 'Core Features', icon: Shield, color: 'bg-blue-100 text-blue-700' },
    { id: 'ai-analytics', name: 'AI & Analytics', icon: Brain, color: 'bg-purple-100 text-purple-700' },
    { id: 'safety-operations', name: 'Safety Operations', icon: Radio, color: 'bg-red-100 text-red-700' },
    { id: 'gamification', name: 'Gamification', icon: Trophy, color: 'bg-amber-100 text-amber-700' },
    { id: 'integrations', name: 'Integrations', icon: Plug, color: 'bg-slate-100 text-slate-700' },
    { id: 'voice-commands', name: 'Voice Commands', icon: Mic, color: 'bg-cyan-100 text-cyan-700' },
    { id: 'subscription', name: 'Billing & Plans', icon: DollarSign, color: 'bg-emerald-100 text-emerald-700' },
  ];

  const tutorials = {
    'getting-started': {
      title: 'Getting Started Guide',
      description: 'Everything you need to know to start using WSafe AI',
      sections: [
        {
          id: 'account-setup',
          title: '1. Account Setup & Login',
          icon: Users,
          content: [
            {
              subtitle: 'Creating Your Account',
              steps: [
                'Navigate to the login page at your organization\'s URL',
                'Click "Register" or "Sign Up"',
                'Enter your work email address',
                'Create a strong password (minimum 8 characters)',
                'Select your role (Safety Manager, Supervisor, Admin)',
                'Click "Create Account" to complete registration'
              ],
              tip: 'Test credentials: Admin@wsafeai.com / Conf3ttiAdm'
            },
            {
              subtitle: 'First Login & Dashboard Overview',
              steps: [
                'Sign in with your credentials',
                'You\'ll see the main Dashboard with key metrics',
                'Risk Score shows your overall safety performance',
                'Quick action cards provide instant access to features',
                'The sidebar navigation lists all available modules'
              ]
            }
          ]
        },
        {
          id: 'navigation',
          title: '2. Navigation & Interface',
          icon: BarChart3,
          content: [
            {
              subtitle: 'Main Dashboard',
              steps: [
                'Key Metrics: Risk Score, Safety Score, Active Audits, Training Progress',
                'Core Safety Tools: Audits, Training, Observations, Knowledge Center',
                'AI & Analytics: Safety AI, Predictive Analytics, Translation Hub',
                'Operations: Safety Operations, Monitoring Hub, ROI Calculator',
                'Engagement: Achievements, Integrations, Meetings'
              ]
            },
            {
              subtitle: 'Sidebar Navigation',
              steps: [
                'Dashboard - Central command center',
                'Audits - Safety inspection management',
                'Training - OSHA courses and quizzes',
                'Observations - Hazard reporting',
                'Analytics Hub - Predictive insights',
                'Achievements - Gamification rewards',
                'Integrations - External system connections'
              ]
            }
          ]
        }
      ]
    },
    'core-features': {
      title: 'Core Safety Features',
      description: 'Master the essential safety management tools',
      sections: [
        {
          id: 'safety-audits',
          title: 'Safety Audits',
          icon: ClipboardCheck,
          content: [
            {
              subtitle: 'Creating a New Audit',
              steps: [
                'Click "New Audit" from Dashboard or Audits page',
                'Select the company/site to audit',
                'Choose audit categories (Forklift, PPE, Fire Safety, etc.)',
                'Answer compliance questions with Yes/No/N/A',
                'Add photos and notes for each finding',
                'Generate comprehensive PDF reports'
              ],
              tip: 'Use the AI Safety Analysis to scan photos for hazards automatically'
            },
            {
              subtitle: 'Audit Categories',
              steps: [
                'Forklift Safety - Equipment inspection and operator compliance',
                'PPE - Personal protective equipment verification',
                'Fire Safety - Extinguishers, exits, and emergency systems',
                'Electrical - Wiring, outlets, and equipment safety',
                'Housekeeping - Workplace cleanliness and organization',
                'Ergonomics - Workstation setup and injury prevention'
              ]
            }
          ]
        },
        {
          id: 'training',
          title: 'Training Center',
          icon: GraduationCap,
          content: [
            {
              subtitle: 'Accessing Training Modules',
              steps: [
                'Navigate to Training from the sidebar',
                'Browse available OSHA courses',
                'Click on a module to start learning',
                'Watch video content and read materials',
                'Complete interactive quizzes',
                'Earn certificates upon completion'
              ]
            },
            {
              subtitle: 'Available Training Topics',
              steps: [
                'OSHA 10/30 Hour Courses',
                'Hazard Communication (HazCom)',
                'Fall Protection',
                'Lockout/Tagout (LOTO)',
                'Confined Space Entry',
                'Fire Prevention and Safety',
                'Personal Protective Equipment',
                'Ergonomics and Injury Prevention'
              ]
            }
          ]
        },
        {
          id: 'observations',
          title: 'Safety Observations',
          icon: Eye,
          content: [
            {
              subtitle: 'Reporting Safety Concerns',
              steps: [
                'Go to Observations from the sidebar',
                'Click "New Observation"',
                'Select observation type (Hazard, Near Miss, Positive)',
                'Describe the situation in detail',
                'Upload photos if available',
                'Assign severity level (Low, Medium, High, Critical)',
                'Submit for review and action'
              ]
            },
            {
              subtitle: 'Tracking & Resolution',
              steps: [
                'View all observations in the dashboard',
                'Filter by status, severity, or date',
                'Assign corrective actions to team members',
                'Track progress through resolution',
                'Close observations when resolved',
                'Generate reports for trend analysis'
              ]
            }
          ]
        }
      ]
    },
    'ai-analytics': {
      title: 'AI & Analytics Features',
      description: 'Leverage artificial intelligence for safety insights',
      sections: [
        {
          id: 'safety-ai',
          title: 'Advanced Safety AI',
          icon: Sparkles,
          content: [
            {
              subtitle: 'AI-Powered Image Analysis',
              steps: [
                'Access from Advanced Tools or Safety AI menu',
                'Upload workplace photos or videos',
                'AI analyzes for safety hazards automatically',
                'Receive detailed compliance reports',
                'Get specific recommendations for improvements',
                'Track historical patterns and trends'
              ],
              tip: 'Supports images, videos, and PDF documents for comprehensive analysis'
            },
            {
              subtitle: 'Analysis Features',
              steps: [
                'Hazard Detection - Identifies safety violations',
                'PPE Verification - Checks for proper equipment usage',
                'Equipment Assessment - Evaluates machinery safety',
                'Environmental Analysis - Detects lighting, ventilation issues',
                'Compliance Scoring - Percentage-based safety rating',
                'Automated Recommendations - AI-generated action items'
              ]
            }
          ]
        },
        {
          id: 'analytics-hub',
          title: 'Analytics & Intelligence Hub',
          icon: Activity,
          content: [
            {
              subtitle: 'Predictive Risk Scoring',
              steps: [
                'Navigate to Analytics Hub',
                'View your overall Risk Score (0-100)',
                'Analyze contributing risk factors',
                'Review AI-generated recommendations',
                'Track risk trends over time',
                'Set alerts for risk threshold changes'
              ]
            },
            {
              subtitle: 'Industry Benchmarking',
              steps: [
                'Select your industry (Construction, Manufacturing, etc.)',
                'Compare your metrics against industry averages',
                'View TRIR, DART, and EMR comparisons',
                'Identify strengths and improvement areas',
                'Track your percentile ranking',
                'Download benchmark reports'
              ]
            },
            {
              subtitle: 'Root Cause Analysis (RCA)',
              steps: [
                'Create new analysis for incidents',
                'Use 5-Why or Fishbone methodology',
                'Document contributing factors',
                'Identify root causes systematically',
                'Develop corrective action plans',
                'Track implementation progress'
              ]
            },
            {
              subtitle: 'Safety ROI Dashboard',
              steps: [
                'Track total safety investment',
                'View cost savings from prevention',
                'Calculate ROI percentage',
                'See incidents prevented estimate',
                'Monitor workers comp savings',
                'Export financial reports'
              ]
            }
          ]
        },
        {
          id: 'translation',
          title: 'Translation Hub',
          icon: Languages,
          content: [
            {
              subtitle: 'Multilingual Communication',
              steps: [
                'Access Translation Hub from sidebar',
                'Type or paste safety messages',
                'Select target languages (Spanish, Chinese, etc.)',
                'Translate instantly with AI',
                'Broadcast to connected channels',
                'Integrate with Telegram, WhatsApp, Slack, Teams'
              ]
            },
            {
              subtitle: 'Supported Integrations',
              steps: [
                'Telegram - Real-time safety alerts',
                'WhatsApp - Team communication',
                'Slack - Workspace notifications',
                'Microsoft Teams - Enterprise messaging',
                'SMS (coming soon) - Direct text alerts'
              ]
            }
          ]
        }
      ]
    },
    'safety-operations': {
      title: 'Safety Operations Center',
      description: 'Manage emergency response, compliance, and monitoring',
      sections: [
        {
          id: 'emergency',
          title: 'Emergency Response',
          icon: AlertTriangle,
          content: [
            {
              subtitle: 'Panic Button & Alerts',
              steps: [
                'Access Safety Operations from sidebar',
                'Find the Emergency section',
                'Red Panic Button triggers immediate alerts',
                'Manage emergency contacts list',
                'Define muster points for evacuations',
                'Configure alert notification chains'
              ],
              tip: 'Test the panic button regularly to ensure proper functionality'
            },
            {
              subtitle: 'Emergency Contacts',
              steps: [
                'Add emergency contacts (First Aid, Fire, Security)',
                'Set priority order for notifications',
                'Include phone numbers and roles',
                'Configure automatic call trees',
                'Update contacts as personnel change'
              ]
            }
          ]
        },
        {
          id: 'compliance',
          title: 'Compliance Management',
          icon: Calendar,
          content: [
            {
              subtitle: 'Compliance Calendar',
              steps: [
                'View all compliance deadlines',
                'Track OSHA requirements',
                'Set reminders for inspections',
                'Monitor certification expirations',
                'Schedule recurring audits',
                'Receive overdue alerts'
              ]
            },
            {
              subtitle: 'Certification Tracking',
              steps: [
                'Manage worker certifications',
                'Track expiration dates',
                'Send renewal reminders',
                'Upload certificate copies',
                'Generate compliance reports',
                'Audit certification status'
              ]
            }
          ]
        },
        {
          id: 'monitoring',
          title: 'Monitoring Hub',
          icon: Radio,
          content: [
            {
              subtitle: 'IoT Sensor Integration',
              steps: [
                'Navigate to Monitoring Hub',
                'View connected sensors',
                'Monitor temperature, humidity, air quality',
                'Set alert thresholds',
                'Track historical readings',
                'Configure automated responses'
              ]
            },
            {
              subtitle: 'Wearable Device Tracking',
              steps: [
                'Connect worker wearables',
                'Monitor vital signs and fatigue',
                'Track location in real-time',
                'Receive fall detection alerts',
                'View individual worker dashboards',
                'Generate safety reports'
              ]
            },
            {
              subtitle: 'Geofencing & Zones',
              steps: [
                'Define safety zones on maps',
                'Set entry/exit rules',
                'Configure hazardous area alerts',
                'Track worker movements',
                'Restrict access based on certification',
                'Log zone violations automatically'
              ]
            },
            {
              subtitle: 'Chemical/SDS Management',
              steps: [
                'Maintain chemical inventory',
                'Store Safety Data Sheets (SDS)',
                'Track chemical locations',
                'Monitor exposure limits',
                'Generate GHS labels',
                'Ensure HazCom compliance'
              ]
            }
          ]
        }
      ]
    },
    'gamification': {
      title: 'Gamification & Achievements',
      description: 'Motivate your team with rewards and recognition',
      sections: [
        {
          id: 'points',
          title: 'Points System',
          icon: Zap,
          content: [
            {
              subtitle: 'Earning Points',
              steps: [
                'Complete safety audits (+50-200 points)',
                'Finish training modules (+25-100 points)',
                'Report safety observations (+10-50 points)',
                'Achieve daily login streaks (+5-25 points)',
                'Complete challenges (+25-250 points)',
                'Help train new team members (+100 points)'
              ]
            },
            {
              subtitle: 'Level Progression',
              steps: [
                'Level 1: 0-99 points (Rookie)',
                'Level 2: 100-249 points (Observer)',
                'Level 3: 250-499 points (Guardian)',
                'Level 4: 500-999 points (Protector)',
                'Level 5: 1000-1999 points (Champion)',
                'Level 6+: 2000+ points (Safety Master)'
              ]
            }
          ]
        },
        {
          id: 'badges',
          title: 'Badges & Achievements',
          icon: Award,
          content: [
            {
              subtitle: 'Badge Categories',
              steps: [
                'Training Badges - First Steps, Training Master',
                'Safety Badges - Hazard Hunter, Safety Champion',
                'Compliance Badges - Audit Ace, Compliance Guru',
                'Leadership Badges - Mentor, Safety Leader',
                'Innovation Badges - Idea Generator, Innovator',
                'Teamwork Badges - Team Player, Collaboration Star'
              ]
            },
            {
              subtitle: 'Badge Tiers',
              steps: [
                '🥉 Bronze - Entry level achievements',
                '🥈 Silver - Intermediate accomplishments',
                '🥇 Gold - Advanced milestones',
                '💎 Platinum - Expert achievements'
              ]
            }
          ]
        },
        {
          id: 'challenges',
          title: 'Challenges & Leaderboards',
          icon: Target,
          content: [
            {
              subtitle: 'Active Challenges',
              steps: [
                'Daily Challenges - Quick tasks for bonus points',
                'Weekly Challenges - Larger goals with bigger rewards',
                'Monthly Challenges - Team-wide objectives',
                'Special Events - Seasonal safety campaigns',
                'Join challenges from Achievements page',
                'Track progress in real-time'
              ]
            },
            {
              subtitle: 'Leaderboards',
              steps: [
                'View global rankings by points',
                'Compare with team members',
                'Filter by department or location',
                'See weekly, monthly, and all-time leaders',
                'Celebrate top performers',
                'Team leaderboards for healthy competition'
              ]
            }
          ]
        }
      ]
    },
    'integrations': {
      title: 'External Integrations',
      description: 'Connect with insurance, HR, and project management systems',
      sections: [
        {
          id: 'insurance',
          title: 'Insurance Integration',
          icon: Shield,
          content: [
            {
              subtitle: 'Supported Providers',
              steps: [
                'Travelers Insurance',
                'Liberty Mutual',
                'The Hartford',
                'Zurich Insurance',
                'AIG',
                'Custom Provider (API)'
              ]
            },
            {
              subtitle: 'Features',
              steps: [
                'Submit claims directly from incidents',
                'Track claim status in real-time',
                'View policy information',
                'Monitor premium impacts',
                'Generate reports for auditors',
                'Sync incident data automatically'
              ]
            }
          ]
        },
        {
          id: 'hr',
          title: 'HR System Integration',
          icon: Users,
          content: [
            {
              subtitle: 'Supported HR Systems',
              steps: [
                'Workday',
                'SAP SuccessFactors',
                'ADP',
                'BambooHR',
                'Gusto',
                'Custom HR API'
              ]
            },
            {
              subtitle: 'Sync Capabilities',
              steps: [
                'Import employee data automatically',
                'Sync training records',
                'Track certification status',
                'Update organizational charts',
                'Manage department assignments',
                'Automate onboarding safety training'
              ]
            }
          ]
        },
        {
          id: 'pm',
          title: 'Project Management Integration',
          icon: ClipboardCheck,
          content: [
            {
              subtitle: 'Supported PM Tools',
              steps: [
                'Jira - Issue tracking & sprints',
                'Asana - Task management',
                'Monday.com - Visual boards',
                'Trello - Kanban boards',
                'ClickUp - All-in-one workspace',
                'Custom PM API'
              ]
            },
            {
              subtitle: 'Safety Task Management',
              steps: [
                'Create safety tasks from incidents',
                'Assign corrective actions',
                'Track completion progress',
                'Set due dates and priorities',
                'Link to audit findings',
                'Sync status bidirectionally'
              ]
            }
          ]
        }
      ]
    },
    'voice-commands': {
      title: 'Voice Commands',
      description: 'Hands-free navigation and safety reporting',
      sections: [
        {
          id: 'setup',
          title: 'Getting Started with Voice',
          icon: Mic,
          content: [
            {
              subtitle: 'Enabling Voice Commands',
              steps: [
                'Look for the microphone button (bottom-right)',
                'Click to start listening (turns red)',
                'Allow microphone access when prompted',
                'Speak commands clearly',
                'Say "show commands" for help',
                'Click again to stop listening'
              ],
              tip: 'Works best in quiet environments with a good microphone'
            }
          ]
        },
        {
          id: 'commands',
          title: 'Available Commands',
          icon: MessageSquare,
          content: [
            {
              subtitle: 'Navigation Commands',
              steps: [
                '"Go to dashboard" - Opens main dashboard',
                '"Go to audits" - Opens audit management',
                '"Go to training" - Opens training center',
                '"Go to observations" - Opens safety observations',
                '"Go to analytics" - Opens Analytics Hub',
                '"Go to gamification" - Opens achievements'
              ]
            },
            {
              subtitle: 'Action Commands',
              steps: [
                '"Start audit" - Begins new safety audit',
                '"Report hazard" - Opens observation form',
                '"Emergency stop" - Triggers emergency alert',
                '"Take photo" - Opens camera for documentation',
                '"Start recording" - Begins audio/video capture',
                '"Stop recording" - Ends recording'
              ]
            },
            {
              subtitle: 'System Commands',
              steps: [
                '"Show commands" - Displays help menu',
                '"Stop listening" - Pauses voice recognition',
                '"Start listening" - Resumes voice recognition'
              ]
            }
          ]
        }
      ]
    },
    'subscription': {
      title: 'Billing & Plans',
      description: 'Manage your subscription and billing',
      sections: [
        {
          id: 'plans',
          title: 'Available Plans',
          icon: DollarSign,
          content: [
            {
              subtitle: 'Plan Comparison',
              steps: [
                'Starter - Up to 25 employees ($19.99/month)',
                'Professional - Up to 100 employees ($29.99/month)',
                'Enterprise - Unlimited employees ($49.99/month)',
                'All plans include: 14-day free trial, no contracts',
                'Professional+: AI analysis, training management, IoT integration',
                'Enterprise: Multi-location, dedicated support, custom integrations'
              ]
            },
            {
              subtitle: 'Managing Your Subscription',
              steps: [
                'View current plan in Settings > Billing',
                'Upgrade or downgrade anytime',
                'Add or remove users',
                'View invoices and payment history',
                'Update payment methods',
                'Cancel subscription (data retained 30 days)'
              ]
            }
          ]
        }
      ]
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Universal Page Header */}
        <PageHeader
          title="Help Center"
          subtitle="Find answers, tutorials, and guides for all WSafe AI features"
          icon={BookOpen}
          variant="purple"
          actions={
            <Badge className="bg-white/20 text-white border-white/30">
              <Sparkles className="w-3 h-3 mr-1" />
              AI-Powered
            </Badge>
          }
        />
        
        {/* AI Search Bar */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask anything... e.g., 'How do I create an audit?'"
                  className="pl-10 bg-slate-50 border-slate-200"
                />
              </div>
              <Button 
                onClick={handleAISearch}
                disabled={isSearching}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Ask AI
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Search Results */}
        {aiSearchResults && (
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Brain className="w-5 h-5" />
                AI Assistant Answer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">{aiSearchResults.answer}</p>
              {aiSearchResults.related_topics?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-slate-500">Related:</span>
                  {aiSearchResults.related_topics.map((topic, i) => (
                    <Badge key={i} variant="outline" className="bg-white">
                      {topic}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Category Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`p-4 rounded-xl border transition-all text-left ${
                  isSelected 
                    ? 'bg-white shadow-lg border-purple-300 ring-2 ring-purple-200' 
                    : 'bg-white hover:shadow-md'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center mb-2`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="font-medium text-sm text-slate-900">{cat.name}</p>
              </button>
            );
          })}
        </div>

        {/* Tutorial Content */}
        {selectedCategory === 'mobile-app' ? (
          <PWAFullGuide />
        ) : tutorials[selectedCategory] && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{tutorials[selectedCategory].title}</CardTitle>
              <CardDescription>{tutorials[selectedCategory].description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {tutorials[selectedCategory].sections.map((section) => {
                const SectionIcon = section.icon;
                const isExpanded = expandedSection === section.id;
                return (
                  <div key={section.id} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
                          <SectionIcon className="w-5 h-5 text-slate-600" />
                        </div>
                        <span className="font-medium text-slate-900">{section.title}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="p-4 space-y-6 bg-white">
                        {section.content.map((item, idx) => (
                          <div key={idx}>
                            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {item.subtitle}
                            </h4>
                            <ol className="space-y-2 ml-6">
                              {item.steps.map((step, stepIdx) => (
                                <li key={stepIdx} className="text-slate-600 text-sm flex items-start gap-2">
                                  <span className="text-purple-500 font-medium">{stepIdx + 1}.</span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                            {item.tip && (
                              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <p className="text-sm text-amber-800 flex items-start gap-2">
                                  <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                  <span><strong>Tip:</strong> {item.tip}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Quick Links Footer */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <CardContent className="py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Video Tutorials
                </h3>
                <p className="text-blue-100 text-sm">
                  Watch step-by-step video guides for all features
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Contact Support
                </h3>
                <p className="text-blue-100 text-sm">
                  Email: support@wsafeai.com
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documentation
                </h3>
                <p className="text-blue-100 text-sm">
                  Access detailed API docs and guides
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default EnhancedHelpCenter;
